import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "./client";

/**
 * A payee, as the autocomplete returns them (`model.md` §3.3).
 *
 * `default_category_id` is what the whole endpoint is for: the category this
 * payee was last filed under, so picking a known name fills the rest of the form
 * in (`project.md` §4). It is absent when there is no history yet, or when the
 * category it would name has since been archived and the picker no longer offers
 * it.
 */
export interface Payee {
  id: string;
  name: string;
  normalized_name: string;
  default_category_id?: string;
  default_is_business?: boolean;
  transaction_count: number;
  last_used_at?: string;
}

interface List<T> {
  items: T[];
}

export const payeesQueryKey = (householdId: string, query: string) =>
  ["households", householdId, "payees", query] as const;

/**
 * Fetches the autocomplete suggestions for what has been typed so far.
 *
 * There is no debounce and no minimum length. The list is a top-N over a table
 * with one row per payee a household has ever used — a few dozen, not a few
 * thousand — and react-query serves repeat prefixes from its own cache, so the
 * request that would need debouncing mostly does not happen. If that stops being
 * true it is K-15's problem, with a measurement behind it.
 */
export function usePayees(householdId: string | undefined, query = "") {
  return useQuery({
    queryKey: payeesQueryKey(householdId ?? "", query),
    queryFn: ({ signal }) => {
      const suffix = query ? `?q=${encodeURIComponent(query)}` : "";
      return apiFetch<List<Payee>>(`/api/v1/households/${householdId}/payees${suffix}`, {
        signal,
      });
    },
    enabled: Boolean(householdId),
    // A payee list changes only when a booking is written, and the suggestions
    // are advisory either way — a stale one costs a keystroke, not a mistake.
    staleTime: 30_000,
  });
}

/**
 * Finds the payee an exactly-typed name refers to.
 *
 * The comparison mirrors `domain.NormalizePayeeName`: case-folded,
 * punctuation-stripped, whitespace collapsed. It is deliberately the same rule
 * rather than a looser one — the pre-fill should fire exactly when the server
 * would resolve the same row, and never on a near miss the user did not choose.
 */
export function matchPayee(payees: Payee[], typed: string): Payee | undefined {
  const key = normalizePayeeName(typed);
  if (!key) return undefined;
  return payees.find((p) => p.normalized_name === key);
}

/** The client-side copy of the server's matching key. */
export function normalizePayeeName(name: string): string {
  const out: string[] = [];
  let separator = false;
  for (const character of name.trim().toLowerCase()) {
    if (/\p{L}|\p{N}/u.test(character)) {
      if (separator && out.length > 0) out.push(" ");
      separator = false;
      out.push(character);
    } else {
      separator = true;
    }
  }
  return out.join("");
}
